import * as yup from 'yup';

import { ISelectorValue } from '../types/shell';
import { USA } from './countries';
import { NO_DEPARTMENT } from './organizations';
import { URL_VARS } from './routing';
import { FALLBACK_CURRENCY_CODE, ZIP_VALIDATION_REGEX } from './shell';

export const USER = 'user';
export const ADMIN = 'admin';
export const SUPER_ADMIN = 'super';
export const ORG_ADMIN = 'org';
export const DEPT_ADMIN = 'dept';
export const DISTRIBUTOR = 'dist';

export const USER_LABEL = 'User';

export const ADDRESS1 = 'address1';
export const ADDRESS2 = 'address2';
export const CITY = 'city';
export const COUNTRY = 'country';
export const EMAIL = 'email';
export const FIRST_NAME = 'first_name';
export const LAST_NAME = 'last_name';
export const PHONE = 'phone';
export const STATE = 'state';
export const ZIP = 'zip';
export const DEPARTMENT_ID = 'department_id';
export const DISTRIBUTOR_ORG_IDS = 'distributor_org_ids';
export const ADMIN_TYPE = 'admin_type';
export const AVAILABLE_BUDGET = 'available_budget';
export const UID = 'uid';
export const CRM_SKIP_ALLOWED = 'crm_skip_allowed';
export const DIGITAL_BALANCE_EMAILS_DISABLED = 'digital_balance_emails_disabled';
export const DISPLAY_CURRENCY = 'display_currency';
export const BUDGET_CURRENCY = 'budget_currency';

export enum UserFilterTypesEnum {
  All,
  Admin,
  User,
}

export const UserSuccessToastMessages = {
  [URL_VARS.NEW]: 'New user was added',
  [URL_VARS.EDIT]: 'User was edited',
};

const fieldNamesForErrorOutput = {
  [ADMIN_TYPE]: 'Admin Type',
  [DEPARTMENT_ID]: 'Department',
  [DISTRIBUTOR_ORG_IDS]: 'Distributor Org IDs',
  [EMAIL]: 'Email',
  [FIRST_NAME]: 'First Name',
  [LAST_NAME]: 'Last Name',
  [ADDRESS1]: 'Address 1',
  [ADDRESS2]: 'Address 2',
  [CITY]: 'City',
  [STATE]: 'State',
  [ZIP]: 'ZIP Code',
  [COUNTRY]: 'Country',
  [PHONE]: 'Phone',
  [AVAILABLE_BUDGET]: 'Available Budget',
};

export const USER_INITIAL_VALUES = {
  [ADMIN_TYPE]: '',
  [DEPARTMENT_ID]: NO_DEPARTMENT,
  [DISTRIBUTOR_ORG_IDS]: [] as string[],
  [EMAIL]: '',
  [FIRST_NAME]: '',
  [LAST_NAME]: '',
  [ADDRESS1]: '',
  [ADDRESS2]: '',
  [CITY]: '',
  [STATE]: '',
  [ZIP]: '',
  [COUNTRY]: '',
  [PHONE]: '',
  [AVAILABLE_BUDGET]: 0,
  [CRM_SKIP_ALLOWED]: false,
  [DISPLAY_CURRENCY]: FALLBACK_CURRENCY_CODE,
  [BUDGET_CURRENCY]: FALLBACK_CURRENCY_CODE,
};

export const USERS_VALIDATION_SCHEMA = yup.object().shape({
  [ADMIN_TYPE]: yup.string().label(fieldNamesForErrorOutput[ADMIN_TYPE]).required(),
  [DEPARTMENT_ID]: yup.string().label(fieldNamesForErrorOutput[DEPARTMENT_ID]).required(),
  [DISTRIBUTOR_ORG_IDS]: yup.array().when(ADMIN_TYPE, {
    is: DISTRIBUTOR,
    then: (sc) =>
      sc
        .of(yup.string().required())
        .min(1, 'This field must have at least 1 value')
        .label(fieldNamesForErrorOutput[DISTRIBUTOR_ORG_IDS]),
    otherwise: (sc) => sc.notRequired(),
  }),
  [EMAIL]: yup.string().email().label(fieldNamesForErrorOutput[EMAIL]).required(),
  [FIRST_NAME]: yup.string().label(fieldNamesForErrorOutput[FIRST_NAME]).required(),
  [LAST_NAME]: yup.string().label(fieldNamesForErrorOutput[LAST_NAME]).required(),
  [ADDRESS1]: yup.string().label(fieldNamesForErrorOutput[ADDRESS1]).required(),
  [ADDRESS2]: yup.string(),
  [CITY]: yup.string().label(fieldNamesForErrorOutput[CITY]).required(),
  [STATE]: yup
    .string()
    .label(fieldNamesForErrorOutput[STATE])
    .when(COUNTRY, {
      is: USA.two_digit_code,
      then: (sc) => sc.required(),
      otherwise: (sc) => sc,
    }),
  [ZIP]: yup
    .string()
    .label(fieldNamesForErrorOutput[ZIP])
    .matches(ZIP_VALIDATION_REGEX, 'Invalid zip format')
    .required()
    .trim(),
  [COUNTRY]: yup.string().label(fieldNamesForErrorOutput[COUNTRY]).required(),
  [PHONE]: yup.string().label(fieldNamesForErrorOutput[PHONE]).required(),
  [AVAILABLE_BUDGET]: yup.number().moreThan(0).label(fieldNamesForErrorOutput[AVAILABLE_BUDGET]).required(),
  [CRM_SKIP_ALLOWED]: yup.boolean(),
  [DISPLAY_CURRENCY]: yup.string(),
  [BUDGET_CURRENCY]: yup.string().required(),
});

export const USER_FORM_REQUIRED_FIELDS_I18L = [] as string[];

export const USER_FORM_REQUIRED_FIELDS_USA = USER_FORM_REQUIRED_FIELDS_I18L.concat(ADDRESS1, STATE);

export const ROLES_SELECT_OPTIONS = (role?: string): ISelectorValue[] => {
  const roles = [
    {
      label: 'User',
      value: USER,
    },
    {
      label: 'Department Admin',
      value: DEPT_ADMIN,
    },
    {
      label: 'Organization Admin',
      value: ORG_ADMIN,
    },
    {
      label: 'Distributor',
      value: DISTRIBUTOR,
    },
  ];

  if (role === SUPER_ADMIN) {
    roles.push({
      label: 'Super Admin',
      value: SUPER_ADMIN,
    });
  }

  return roles;
};

export const USERS_EDITING_PERMISSION_LIST: Record<string, string[]> = {
  [SUPER_ADMIN]: [SUPER_ADMIN, DISTRIBUTOR, ORG_ADMIN, DEPT_ADMIN, USER, ''],
  [DISTRIBUTOR]: [ORG_ADMIN, DEPT_ADMIN, USER, ''],
  [ORG_ADMIN]: [ORG_ADMIN, DEPT_ADMIN, USER, ''],
};

export const USER_REASSIGN_REQUIRED_ERROR_CODE = 'UserReassignRequired';

export const DISPLAY_CURRENCY_HINT =
  "All prices in the portal for this user will be displayed in the selected currency.To view prices in the original currency, choose 'None'.";

export const COLUMN_IDS = {
  USER: 'user',
  INTEGRATIONS: 'integrations',
  ROLE: 'role',
  DEPARTMENT: 'department',
  BUDGET_LEFT: 'budget_left',
  BUDGET_SPENT: 'budget_spent',
  BUDGET_TOTAL: 'budget_total',
  LAST_LOGIN: 'last_login',
  ACTIONS: 'actions',
  PRODUCTION_TYPE: 'production_type',
};

export const VISIBLE_USERS_COLS_INITIAL_STATE = {
  [COLUMN_IDS.USER]: true,
  [COLUMN_IDS.INTEGRATIONS]: true,
  [COLUMN_IDS.ROLE]: true,
  [COLUMN_IDS.DEPARTMENT]: true,
  [COLUMN_IDS.BUDGET_LEFT]: true,
  [COLUMN_IDS.BUDGET_SPENT]: false,
  [COLUMN_IDS.BUDGET_TOTAL]: true,
  [COLUMN_IDS.LAST_LOGIN]: true,
  [COLUMN_IDS.ACTIONS]: true,
  [COLUMN_IDS.PRODUCTION_TYPE]: false,
};

export const USER_ROLE_FILTER_OPTIONS = [
  { label: 'User', value: USER },
  { label: 'Admin', value: ORG_ADMIN },
  { label: 'Super', value: SUPER_ADMIN },
  { label: 'Department', value: DEPT_ADMIN },
  { label: 'Distributor', value: DISTRIBUTOR },
];
